import { plural } from '../appRouteOfferCore.js';

const ptCredits = plural('crédito', 'créditos');

// Brazilian Portuguese offer copy for the prerendered /pricing and /free-credits pages,
// the credit-usage FAQ and the Vectorizer allowance answer. `c` is the
// offer context from appRouteOfferCore.js (catalog prices, credit costs,
// free allowances), so no number is written here by hand.
export const APP_ROUTE_OFFER_COPY_PT = {
  pricing: (c) => ({
    title: 'Planos de preços',
    intro: [
      `Planos mensais de créditos do ${c.first.name} (${c.first.credits} créditos por ${c.first.monthly}) ao ${c.last.name} (${c.last.credits} por ${c.last.monthly}), ${c.discount}% de economia no pagamento anual e pacote avulso de ${c.topUpCredits} créditos por ${c.topUpPrice}.`,
      `Com a Análise rápida, que vem selecionada por padrão, cada imagem usa ${ptCredits(c.fast)}. Os preços estão em USD; o checkout mostra o total na sua moeda local antes do pagamento.`
    ],
    bullets: [
      `${c.planCount} planos mensais de créditos, de ${c.first.credits} a ${c.last.credits} créditos por mês`,
      `O pagamento anual economiza ${c.discount}% e continua adicionando créditos todo mês`,
      `Pacote avulso: ${c.topUpCredits} créditos por ${c.topUpPrice}, sem prazo de validade`,
      `A partir de ${ptCredits(c.fast)} por imagem com a Análise rápida, selecionada por padrão`
    ],
    links: [
      { href: '/metadata', label: 'Experimente o Metadata Studio' },
      { href: '/free-credits', label: 'Ganhe créditos grátis' },
      { href: '/help', label: 'Perguntas frequentes sobre preços' }
    ],
    sections: [
      {
        title: 'Planos mensais de créditos e preços',
        paragraphs: [
          'Os planos se diferenciam pela quantidade de créditos adicionados a cada mês. Os créditos da assinatura são renovados todo mês, e os créditos de assinatura não usados não passam para o mês seguinte.'
        ],
        bullets: c.plans.map((plan) => (plan.yearly
          ? `${plan.name}: ${plan.credits} créditos por mês por ${plan.monthly}/mês, ou ${plan.yearly} por ano (${plan.yearlyPerMonth}/mês)`
          : `${plan.name}: ${plan.credits} créditos por mês por ${plan.monthly}/mês`))
      },
      {
        title: 'Pagamento anual',
        paragraphs: [
          `Pagar anualmente custa ${c.discount}% menos que doze pagamentos mensais. A cobrança é feita uma vez por ano, e os créditos do plano continuam sendo adicionados todo mês.`
        ]
      },
      {
        title: 'Pacote avulso de créditos',
        paragraphs: [
          `Compre ${c.topUpCredits} créditos por ${c.topUpPrice} em um único pagamento. Os créditos comprados nunca expiram, funcionam sem assinatura e se somam aos créditos de um plano ativo.`
        ]
      },
      {
        title: 'O que um crédito cobre',
        paragraphs: [
          'Os créditos são descontados por arquivo, de acordo com o nível de análise escolhido. A Análise rápida vem selecionada por padrão.'
        ],
        bullets: [
          `Análise rápida: ${ptCredits(c.fast)} por imagem (padrão)`,
          `Análise padrão: ${ptCredits(c.standard)} por imagem`,
          `Análise aprofundada: ${ptCredits(c.deep)} por imagem`,
          `Vídeo: 1 crédito a cada ${c.videoSeconds} segundos, arredondado para cima, até ${c.maxVideoSeconds} segundos por clipe`,
          `Exemplo: os ${c.example.credits} créditos mensais do ${c.example.name} cobrem cerca de ${c.example.fast} imagens na Análise rápida, ${c.example.standard} na Análise padrão ou ${c.example.deep} na Análise aprofundada`
        ]
      }
    ],
    faq: [
      {
        q: 'Quanto custa o Stocktag?',
        a: `Os planos mensais começam em ${c.first.monthly} por ${c.first.credits} créditos (${c.first.name}) e vão até ${c.last.monthly} por ${c.last.credits} créditos (${c.last.name}). O pagamento anual economiza ${c.discount}%, e o pacote avulso de ${c.topUpCredits} créditos custa ${c.topUpPrice}. Os preços estão em USD; o checkout mostra o total na sua moeda local.`
      },
      'creditUsage',
      {
        q: 'Os créditos expiram?',
        a: 'Os créditos da assinatura são renovados todo mês, e os não usados não passam para o mês seguinte. Os créditos do pacote avulso nunca expiram.'
      },
      {
        q: 'Posso testar o Stocktag de graça?',
        a: `Sim. Contas novas elegíveis recebem ${c.welcome} créditos grátis, e a demonstração para visitantes na página inicial gera um resultado de exemplo para uma imagem, sem cadastro.`
      },
      {
        q: 'Os planos anuais adicionam todos os créditos de uma vez?',
        a: 'Não. Os planos anuais são cobrados uma vez por ano e adicionam os créditos do plano todo mês.'
      }
    ]
  }),
  creditUsageFaq: (c) => ({
    q: 'Quantos créditos uma imagem usa?',
    a: `Uma imagem usa ${ptCredits(c.fast)} na Análise rápida (padrão), ${c.standard} na Análise padrão e ${c.deep} na Análise aprofundada. Vídeos usam 1 crédito a cada ${c.videoSeconds} segundos.`
  }),
  freeCredits: (c) => ({
    title: 'Créditos Grátis',
    intro: [
      `Contas novas elegíveis recebem ${c.welcome} créditos grátis, e cada indicação elegível rende ${c.referral} créditos. Teste títulos, descrições e palavras-chave nas suas próprias imagens.`,
      `Na Análise rápida, selecionada por padrão, ${c.welcome} créditos rendem ${c.welcomeImages} imagens. Quer ver um resultado antes de criar a conta? A demonstração para visitantes na página inicial processa uma imagem sem cadastro.`
    ],
    bullets: [
      `${c.welcome} créditos grátis para contas novas elegíveis`,
      `${c.referral} créditos por indicação elegível`,
      'Demonstração grátis para visitantes com uma imagem, sem cadastro',
      `Vectorizer: ${c.vectorFree} imagens grátis por dia com login`
    ],
    links: [
      { href: '/pricing', label: 'Detalhes de preços' },
      { href: '/metadata', label: 'Iniciar fluxo de trabalho de metadados' },
      { href: '/help', label: 'Como funcionam os créditos' }
    ],
    sections: [
      {
        title: 'Como funcionam os créditos de boas-vindas',
        paragraphs: [
          `Crie uma conta e conclua as verificações do cadastro. Contas novas elegíveis recebem ${c.welcome} créditos, o suficiente para ${c.welcomeImages} imagens na Análise rápida.`
        ],
        bullets: [
          'Use-os para títulos, descrições e palavras-chave no Metadata Studio',
          `A Análise rápida usa ${ptCredits(c.fast)} por imagem, a padrão ${c.standard} e a aprofundada ${c.deep}`,
          'Revise os resultados e exporte em CSV ou XMP para seus marketplaces'
        ]
      },
      {
        title: 'Ganhe créditos indicando outras pessoas',
        paragraphs: [
          `Compartilhe seu link de indicação pessoal nesta página. Quando alguém cria uma conta pelo link e passa nas verificações necessárias, você recebe ${c.referral} créditos automaticamente.`
        ],
        bullets: [
          'Copie ou compartilhe seu link na página Créditos Grátis',
          'Acompanhe o total de indicações e os créditos ganhos',
          'A recompensa é concedida uma vez por conta nova elegível'
        ]
      },
      {
        title: 'Melhores formas de testar',
        paragraphs: [
          'Rode um lote representativo e revise a ordem das palavras-chave, as descrições e os formatos de exportação para ver como o fluxo se encaixa no seu portfólio.'
        ],
        bullets: [
          'Teste vários marketplaces',
          'Compare conjuntos de palavras-chave por intenção de busca',
          'Avalie as exportações em CSV ou XMP'
        ]
      }
    ],
    faq: [
      {
        q: 'Como consigo créditos grátis?',
        a: `Crie uma conta no Stocktag. Contas novas elegíveis recebem ${c.welcome} créditos após o cadastro, e você ganha ${c.referral} créditos por cada indicação elegível.`
      },
      {
        q: 'Posso testar o Stocktag sem me cadastrar?',
        a: 'Sim. A demonstração para visitantes na página inicial gera um resultado de metadados de exemplo para uma imagem, uma vez a cada 24 horas, sem conta. Para criar o resultado, a imagem é enviada ao serviço de metadados para visitantes do Stocktag.'
      },
      {
        q: 'O que acontece quando os créditos grátis acabam?',
        a: `Escolha um plano mensal a partir de ${c.first.monthly} por ${c.first.credits} créditos ou compre um pacote avulso de ${c.topUpCredits} créditos por ${c.topUpPrice}. Créditos avulsos nunca expiram.`
      },
      'creditUsage'
    ]
  }),
  vectorizerAllowance: (c) => `Sim. Você pode converter um arquivo ou um lote inteiro. Contas conectadas vetorizam ${c.vectorFree} imagens por dia de graça; cada imagem adicional usa ${ptCredits(c.vectorPaid)}.`
};

export default APP_ROUTE_OFFER_COPY_PT;
